<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="650px" :persistent="isFetching">
      <v-card dense>
        <v-toolbar elevation="0">
          <v-toolbar-title
            class="body-bold-medium neutral-20-dark--text"
            style="padding-left: 10px"
          >
            {{ title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn :disabled="isFetching" icon @click="handleCloseModal()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="px-7 py-0">
          <v-row no-gutters>
            <v-col
              cols="12"
              class="body-regular-small neutral-40-light--text"
              v-html="
                $i18next.t(
                  'message.ai.subtitles.deleteModal.confirmDescription'
                )
              "
            >
            </v-col>
            <v-col cols="12" class="my-3 mb-6">
              <b>{{
                $t('message.pages.video.deleteModal.confirmRemoveMessage2')
              }}</b>
            </v-col>
            <v-col cols="12">
              <v-input
                v-model="password"
                class="pr-2"
                :label="$t('message.password')"
                autofocus
                autocomplete="off"
                :error="errors.invalidAccountError"
                :messages="loginPasswordErrorMessage"
                :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPass ? 'text' : 'password'"
                @click:append="showPass = !showPass"
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="errors.invalidAccountError"
                  >
                    <v-icon color="red" size="18" class="pb-1">
                      mdi-alert-circle
                    </v-icon>
                    {{ message }}
                  </span>
                </template>
              </v-input>
            </v-col>
            <v-col cols="12">
              <v-checkbox
                v-model="checkboxConfirmation"
                class="pt-0 mt-0"
                hide-details
              >
                <template v-slot:label>
                  <span style="font-size: 14px">{{
                    $t('message.pages.video.deleteModal.checkboxTextDelete')
                  }}</span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-col cols="12" class="mt-0 pt-0">
          <v-card
            class="d-flex align-center justify-start ml-5"
            width="100%"
            height="20px"
            flat
          >
            <span class="error--text caption" v-if="errors.removeError">
              {{ $t('message.pages.video.deleteModal.errors.removeError') }}
              <br />
              {{ this.errMsg.removeErrorMsg }}
            </span>
          </v-card>
        </v-col>
        <v-divider></v-divider>
        <v-card-actions class="px-7 pb-6">
          <v-spacer></v-spacer>
          <base-btn
            class="mr-4"
            type="tertiary"
            @click="handleCloseModal"
            :disabled="isFetching"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <v-tooltip bottom :disabled="checkboxConfirmation && password">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <base-btn
                  :disabled="isFetching || !checkboxConfirmation || !password"
                  :loading="isFetching"
                  @click="confirmUserPassword"
                >
                  <span>{{ $t('message.confirm') }}</span>
                </base-btn>
              </div>
            </template>
            <span>{{ $t('message.fieldAlertProceed') }}</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import i18n from '@/translations/lang'

export default {
  name: 'AiConfirmDelete',
  props: {
    title: {
      type: String,
      required: true,
    },
    modalKey: {
      type: String,
      required: true,
    },
    onConfirmLogin: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isFetching: false,
    open: false,
    checkboxConfirmation: false,
    showPass: false,
    password: '',
    errors: {
      invalidAccountError: false,
      unexpectedError: false,
      internalErrorException: false,
      removeError: false,
    },
    errMsg: {
      removeErrorMsg: '',
    },
    unknownError: '',
  }),
  mounted() {
    this.$root.$on(
      `openAiDeleteConfirmModal${this.modalKey}`,
      () => (this.open = true)
    )
    this.$root.$on(`closeAiDeleteConfirmModal${this.modalKey}`, () =>
      this.handleCloseModal()
    )
  },
  beforeDestroy() {
    this.$root.$off(`openAiDeleteConfirmModal${this.modalKey}`)
    this.$root.$off(`closeAiDeleteConfirmModal${this.modalKey}`)
  },
  watch: {
    open(value) {
      if (!value) {
        this.handleCloseModal()
      }
    },
  },
  computed: {
    userInfo() {
      return this.$store.getters['session/userAttributes']
    },
    loginPasswordErrorMessage() {
      if (this.errors.invalidAccountError) {
        return i18n.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }
      if (this.errors.internalErrorException) {
        return i18n.t('message.pages.video.deleteModal.errors.internalError')
      }
      if (this.errors.unexpectedError) {
        return i18n.t('message.errors.unexpectedError') + this.unknownError
      }
      return ''
    },
  },
  methods: {
    ...mapActions('session', ['signInUser', 'fetchSession']),
    async confirmUserPassword() {
      this.isFetching = true
      try {
        await this.signInUser({
          username: this.userInfo.email,
          password: this.password,
        })
        await this.onConfirmLogin()
        this.isFetching = false
        this.handleCloseModal()
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError?.message?.includes('Network Error')) {
            this.changePasswordLoading = false
            this.unknownError = 'Code: 500'
            this.newPasswordError.newPasswordError = true
          }
        }
        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.invalidAccountError = true
        }
        if (error.code === 'InternalErrorException') {
          this.errors.internalErrorException = true
        }
      }
      this.isFetching = false
    },
    handleCloseModal() {
      this.open = false
      this.password = ''
      this.showPass = false
      this.checkboxConfirmation = false
      this.errors = {
        invalidAccountError: false,
        unexpectedError: false,
        internalErrorException: false,
        removeError: false,
      }
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .v-text-field__slot .v-label {
  margin-left: -10px;
}
</style>
